import Phaser from 'phaser';

import { ResizeMetrics } from '../../model/resize-metrics';
import { TwentyOneMainScene } from '../../scenes/twentyone-main-scene';
import { ChevronHintViewModel } from './chevron-hint-view-model';
import { Positioning } from '../../model/positioning';
import { UxMetrics } from '../../model/ux-metrics';
// import { DebugContainerVisual } from '../debug/debug-container-visual';

export class ChevronHintView extends Phaser.GameObjects.Container {
  private rectangle: Phaser.GameObjects.Rectangle;
  private chevrons: Phaser.GameObjects.Image[] = [];

  constructor(
    scene: TwentyOneMainScene,
    x: number,
    y: number,
    private viewModel: ChevronHintViewModel,
  ) {
    super(scene, x, y);

    // new DebugContainerVisual(scene, this);

    this.rectangle = scene.add.rectangle(0, 0, 0, 0, 0x0000ff).setAlpha(0.0).setOrigin(0.5, 0.5);
    this.add(this.rectangle);

    for (let i = 0; i < 5; i++) {
      const chevron = scene.add.image(0, 0, viewModel.chevronAsset.type).setAlpha(0).setOrigin(0.5);
      this.chevrons.push(chevron);
    }
    this.add(this.chevrons);

    this.setName(ChevronHintView.name);
    scene.add.existing(this);
  }

  public onResize(resizeMetrics: ResizeMetrics): void {
    const scaleFactor = resizeMetrics.scalingMetrics.scaleFactor;

    const columnStackRect = Positioning.localColumnStackRect(
      resizeMetrics.layoutConfig,
      resizeMetrics.scalingMetrics,
    );

    this.setSize(columnStackRect.width, columnStackRect.height);
    this.rectangle.setSize(columnStackRect.width, columnStackRect.height);

    const asset = this.viewModel.chevronAsset;
    const chevronWidth = asset.dimensions.width * scaleFactor;
    const chevronHeight = asset.dimensions.height * scaleFactor;
    const spacing = resizeMetrics.layoutConfig.paddingXsm * scaleFactor;

    // Set sizes and positions of chevrons
    this.chevrons.forEach((chevron, index) => {
      chevron.setDisplaySize(chevronWidth, chevronHeight);

      const step = chevronHeight + spacing;
      const y = columnStackRect.height * 0.5 - chevronHeight * 0.5 - index * step;

      chevron.setPosition(0, y - spacing);
    });
  }

  public hideChevrons(instant: boolean): void {
    this.chevrons.forEach((chevron) => {
      if (instant) {
        chevron.setAlpha(0);
      } else {
        this.scene.tweens.add({
          targets: chevron,
          alpha: 0,
          duration: UxMetrics.ChevronHideDuration,
          ease: Phaser.Math.Easing.Sine.InOut,
        });
      }
    });
  }

  public activateChevrons(fullCycle: boolean, instant: boolean): void {
    const duration = UxMetrics.ChevronFlashDuration;
    const baseDelay = duration * 0.4;
    const peakAlpha = 0.3;

    if (instant) {
      this.animateFinalTwo(duration, baseDelay, true);
      return;
    }

    this.chevrons.forEach((c) => {
      this.scene.tweens.killTweensOf(c);
      c.setAlpha(0);
    });

    if (fullCycle) {
      this.chevrons.forEach((chevron, index) => {
        this.scene.tweens.add({
          targets: chevron,
          alpha: { from: 0, to: peakAlpha },
          duration,
          yoyo: true,
          ease: Phaser.Math.Easing.Sine.InOut,
          delay: baseDelay * index,
          onComplete: () => {
            if (index === this.chevrons.length - 1) {
              // Start bottom two chevrons final pulse after top one finishes
              this.animateFinalTwo(duration, baseDelay);
            }
          },
        });
      });
    } else {
      this.animateFinalTwo(duration, baseDelay);
    }
  }

  private animateFinalTwo(duration: number, delay: number, instant = false): void {
    const targetAlphas = [0.1, 0.3]; // bottom → top of the two
    const bottomTwo = this.chevrons.slice(0, 2);

    bottomTwo.forEach((chevron, i) => {
      if (instant) {
        chevron.setAlpha(targetAlphas[i]);
        return;
      } else {
        this.scene.tweens.add({
          targets: chevron,
          alpha: { from: 0, to: targetAlphas[i] },
          duration,
          ease: Phaser.Math.Easing.Sine.InOut,
          delay: i * delay,
        });
      }
    });
  }
}
